import { differenceInCalendarDays } from 'date-fns';
import { tenantSendStatus } from '@/lib/tenantSend';
import { pmLabel } from '@/lib/propertyManagerGroups';
import { INACTIVE_STATUSES } from '@/lib/projectStatus';

/**
 * The plan sets and sign packages sent back "Revise & Resubmit" and not yet
 * resubmitted - the Dashboard's Awaiting Revisions list.
 *
 * Once a package is sent back it leaves Pending Reviews, and nothing else on
 * the Dashboard showed it: the next thing to happen is the tenant's, through
 * the Property Manager, and that is exactly what goes quiet. This list is how
 * the Construction Manager follows it up, oldest first, with whether the
 * Property Manager has recorded passing the decision on (lib/tenantSend.js).
 *
 * A package on an archived, complete, on hold or canceled project is left
 * out, the same projects the Active Projects count leaves out: nobody is
 * expected to be chasing them.
 */

/** The audit trail entry of the latest decision; see PlanReview. */
function latestDecision(plan) {
  const trail = Array.isArray(plan.audit_trail) ? plan.audit_trail : [];
  for (let i = trail.length - 1; i >= 0; i -= 1) {
    if (trail[i] && trail[i].approval_status) return trail[i];
  }
  return null;
}

/**
 * `[{ plan, project, kind, propertyManager, sentBackAt, daysWaiting, sent }]`, longest waiting
 * first. `projects` should already carry `property_manager_label` (withPmNames)
 * when a name is wanted rather than the stored email. `sentBackAt` and
 * `daysWaiting` are null for a package whose decision has no timestamp; those
 * go last.
 */
export function awaitingRevisions(plans, projects, today = new Date()) {
  const projectById = new Map(projects.map(p => [p.id, p]));
  const rows = [];
  for (const plan of plans) {
    if (plan.status !== 'revisions_required') continue;
    const project = projectById.get(plan.project_id);
    if (!project || project.archived || INACTIVE_STATUSES.includes(project.status)) continue;
    const decision = latestDecision(plan);
    const sentBackAt = decision?.timestamp ? new Date(decision.timestamp) : null;
    const valid = sentBackAt && !Number.isNaN(sentBackAt.getTime());
    rows.push({
      plan,
      project,
      kind: plan.discipline === 'signage' ? 'Signs' : 'Plans',
      propertyManager: pmLabel(project),
      sentBackAt: valid ? sentBackAt : null,
      daysWaiting: valid ? differenceInCalendarDays(today, sentBackAt) : null,
      sent: tenantSendStatus(plan).sent,
    });
  }
  return rows.sort((a, b) => {
    if (a.sentBackAt && b.sentBackAt) return a.sentBackAt - b.sentBackAt;
    if (a.sentBackAt) return -1;
    if (b.sentBackAt) return 1;
    return 0;
  });
}

/** "Sent back today", "Sent back 1 day ago", "Sent back 12 days ago". */
export function waitingLabel(daysWaiting) {
  if (daysWaiting == null) return 'Sent back';
  if (daysWaiting <= 0) return 'Sent back today';
  return `Sent back ${daysWaiting} day${daysWaiting === 1 ? '' : 's'} ago`;
}
